// Shared data layer for the monitoring dashboard's period-based tabs
// (Overview / Movements / Report). One fetch of the project report powers all
// three, keyed by the selected period, so switching tabs never refetches.

import { useState, useEffect, useCallback } from 'react';

export interface ReportSignal {
  id: number;
  date: string;
  type: string;
  type_label: string;
  priority: string;
  status: string;
  ai_category: string | null;
  ai_category_label: string | null;
  ai_confidence: number | null;
  candidate_name: string | null;
  candidate_job_title: string | null;
  candidate_company: string | null;
  candidate_location: string | null;
  candidate_linkedin_url: string | null;
  title: string;
  message: string;
  company: string | null;
  from_company: string | null;
  to_company: string | null;
  move_date: string | null;
  position: string | null;
  risk_score: number | null;
  risk_tier: string | null;
  detail: string | null;
  changes_summary: string | null;
}

export interface ReportMovement {
  candidate_id: number;
  candidate_name: string;
  candidate_linkedin_url: string | null;
  candidate_location: string | null;
  candidate_lat: number | null;
  candidate_lng: number | null;
  from_company: string | null;
  to_company: string | null;
  from_position: string | null;
  to_position: string | null;
  movement_type: string;
  date: string;
  to_industry: string | null;
  to_employee_count: string | null;
}

export interface ReportCompanyGroup {
  company: string;
  count: number;
  industry?: string | null;
  employee_count?: string | null;
  people: Array<{
    name: string;
    title: string | null;
    from?: string | null;
    to?: string | null;
    date: string;
    linkedin: string | null;
  }>;
}

export interface MonitoringReportData {
  project: { id: number; name: string };
  period: { from: string; to: string };
  summary: {
    total: number;
    byType: Record<string, number>;
    byPriority: Record<string, number>;
    totalMovements: number;
    companiesGaining: number;
    companiesLosing: number;
  };
  // The window of the same length immediately before this one — for trend deltas.
  previous?: {
    from: string;
    to: string;
    total: number;
    byType: Record<string, number>;
    totalMovements: number;
    companiesGaining: number;
    companiesLosing: number;
  } | null;
  signals: ReportSignal[];
  movements: ReportMovement[];
  arrivals: ReportCompanyGroup[];
  departures: ReportCompanyGroup[];
}

export const SIGNAL_TYPE_LABELS: Record<string, string> = {
  job_change: 'Job Change',
  promotion: 'Promotion',
  company_movement: 'Company Movement',
  open_to_work: 'Open to Work',
  high_risk_candidate: 'High Flight Risk',
  critical_risk_candidate: 'Critical Flight Risk',
  profile_change: 'Profile Update',
  new_hire_detected: 'New Hire',
  company_hiring_trend: 'Company Trend',
  cohort_movement: 'Market Event',
};

export const AI_CATEGORY_LABELS: Record<string, string> = {
  genuine_promotion: 'Confirmed promotion',
  new_role_same_employer: 'New role, same employer',
  profile_refresh_only: 'Profile refresh',
  open_to_work_signal: 'Open to work',
  side_venture_added: 'Side venture',
  acquisition_or_merger: 'Possible acquisition',
  team_liftout: 'Team lift-out',
  company_rebrand: 'Likely rebrand',
  organic_moves: 'Organic moves',
  unclear: 'Unclear',
};

// ── Periods ────────────────────────────────────────────────────────────────
// Quarter chips in the MKH style: the current quarter plus the three before
// it, alongside rolling windows. `to` for the current quarter is clamped to
// today so the backend window never reaches into the future.

export interface ReportPeriod {
  key: string;
  label: string;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export function buildPeriods(now: Date = new Date()): ReportPeriod[] {
  const periods: ReportPeriod[] = [
    {
      key: 'last30',
      label: 'Last 30 days',
      from: isoDay(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)),
      to: isoDay(now),
    },
  ];
  let year = now.getUTCFullYear();
  let q = Math.floor(now.getUTCMonth() / 3);
  for (let i = 0; i < 4; i++) {
    const start = new Date(Date.UTC(year, q * 3, 1));
    const end = new Date(Date.UTC(year, q * 3 + 3, 0)); // last day of the quarter
    periods.push({
      key: `q${q + 1}-${year}`,
      label: `Q${q + 1} ${year}`,
      from: isoDay(start),
      to: isoDay(end > now ? now : end),
    });
    q--;
    if (q < 0) { q = 3; year--; }
  }
  periods.push({
    key: 'last12m',
    label: 'Last 12 months',
    from: isoDay(new Date(Date.UTC(now.getUTCFullYear() - 1, now.getUTCMonth(), now.getUTCDate()))),
    to: isoDay(now),
  });
  return periods;
}

export function formatPeriodRange(period: { from: string; to: string }): string {
  const fmt = (v: string) =>
    new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(period.from)} – ${fmt(period.to)}`;
}

// ── Report fetch hook ──────────────────────────────────────────────────────

export function useMonitoringReport(projectId: number, from: string, to: string) {
  const [data, setData] = useState<MonitoringReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/monitoring/projects/${projectId}/report?from=${from}&to=${to}&compare=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to load report');
      setData(await res.json());
    } catch (e: any) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [projectId, from, to]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load };
}
